import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Upload, X, Image, FileText, Tag, File } from 'lucide-react'

export default function ArtifactUpload({ isOpen, onClose, onSuccess }) {
  const { accessToken } = useAuth()
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    tags: ''
  })
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [fileType, setFileType] = useState('') // 'image' or 'pdf'
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  const categories = [
    'Ceremonial',
    'Household',
    'Jewelry',
    'Tools',
    'Clothing',
    'Musical Instruments',
    'Weapons',
    'Art',
    'Religious',
    'Documents', // New category for PDFs
    'Other'
  ]

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError('File size must be less than 10MB')
        return
      }
      
      // Check if it's an image or PDF
      const isImage = file.type.startsWith('image/')
      const isPDF = file.type === 'application/pdf'
      
      if (!isImage && !isPDF) {
        setError('Please select an image file (PNG, JPG, GIF) or PDF document')
        return
      }

      setSelectedFile(file)
      setError('')
      
      if (isImage) {
        setFileType('image')
        // Create preview for images
        const reader = new FileReader()
        reader.onload = (e) => setPreviewUrl(e.target.result)
        reader.readAsDataURL(file)
      } else if (isPDF) {
        setFileType('pdf')
        setPreviewUrl(null) // No preview for PDFs
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!selectedFile) {
      setError('Please select an image or PDF file')
      return
    }

    if (!formData.title.trim() || !formData.description.trim() || !formData.category) {
      setError('Please fill in all required fields')
      return
    }

    setUploading(true)
    setError('')

    try {
      // Debug: Log the token being used
      console.log('Access token:', accessToken ? 'Token present' : 'No token')
      console.log('API Endpoint:', import.meta.env.VITE_API_ENDPOINT)
      
      // Step 1: Get presigned URL for file upload
      const uploadUrlResponse = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/artifacts/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          fileName: selectedFile.name,
          fileType: selectedFile.type
        })
      })

      console.log('Upload URL Response status:', uploadUrlResponse.status)
      
      if (!uploadUrlResponse.ok) {
        const errorText = await uploadUrlResponse.text()
        console.error('Upload URL Error:', errorText)
        throw new Error(`Failed to get upload URL: ${uploadUrlResponse.status} ${errorText}`)
      }

      const uploadUrlData = await uploadUrlResponse.json()
      
      if (!uploadUrlData.success) {
        throw new Error(uploadUrlData.message || 'Failed to get upload URL')
      }

      // Step 2: Upload file to S3
      const uploadResponse = await fetch(uploadUrlData.data.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': selectedFile.type
        },
        body: selectedFile
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file')
      }

      // Step 3: Create artifact record
      const artifactData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        imageKey: uploadUrlData.data.key, // This will be renamed to 'fileKey' in backend
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        metadata: {
          originalFileName: selectedFile.name,
          fileSize: selectedFile.size,
          fileType: selectedFile.type,
          contentType: fileType // 'image' or 'pdf'
        }
      }

      const createResponse = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/artifacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(artifactData)
      })

      if (!createResponse.ok) {
        throw new Error('Failed to create artifact record')
      }

      const createData = await createResponse.json()
      
      if (!createData.success) {
        throw new Error(createData.message || 'Failed to create artifact')
      }

      // Success!
      onSuccess && onSuccess(createData.data)
      handleClose()
      
    } catch (err) {
      console.error('Upload error:', err)
      setError(err.message || 'Failed to upload artifact')
    } finally {
      setUploading(false)
    }
  }

  const handleClose = () => {
    setFormData({ title: '', description: '', category: '', tags: '' })
    setSelectedFile(null)
    setPreviewUrl(null)
    setFileType('')
    setError('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <Upload className="w-6 h-6 text-primary-600" />
              <span>Upload Cultural Artifact</span>
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Artifact File (Image or PDF) *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                {selectedFile ? (
                  <div className="space-y-4">
                    {fileType === 'image' && previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="max-h-48 mx-auto rounded-lg object-cover"
                      />
                    ) : fileType === 'pdf' ? (
                      <div className="flex flex-col items-center space-y-2">
                        <File className="w-16 h-16 text-red-500" />
                        <span className="text-sm font-medium text-gray-700">PDF Document</span>
                      </div>
                    ) : null}
                    <div className="text-sm text-gray-600">
                      {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(null)
                        setPreviewUrl(null)
                        setFileType('')
                      }}
                      className="text-primary-600 hover:text-primary-700 text-sm"
                    >
                      Choose different file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-center space-x-4">
                      <Image className="w-12 h-12 text-gray-400" />
                      <File className="w-12 h-12 text-gray-400" />
                    </div>
                    <div className="text-gray-600">
                      <label htmlFor="file-upload" className="cursor-pointer text-primary-600 hover:text-primary-700">
                        Click to upload
                      </label>
                      <span> or drag and drop</span>
                    </div>
                    <p className="text-xs text-gray-500">Images (PNG, JPG, GIF) or PDF documents up to 10MB</p>
                  </div>
                )}
                <input
                  id="file-upload"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>

            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Artifact Title *
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="title"
                  name="title"
                  type="text"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  className="input-field pl-10"
                  placeholder="e.g., Traditional Kalenjin Shield"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                required
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
                className="input-field"
                placeholder="Describe the artifact's history, significance, and cultural importance..."
              />
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                id="category"
                name="category"
                required
                value={formData.category}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="">Select a category</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
                Tags (optional)
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="tags"
                  name="tags"
                  type="text"
                  value={formData.tags}
                  onChange={handleInputChange}
                  className="input-field pl-10"
                  placeholder="e.g., warrior, ceremony, traditional (separate with commas)"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Separate multiple tags with commas
              </p>
            </div>

            {/* Submit Buttons */}
            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="flex-1 btn-primary py-3 flex items-center justify-center space-x-2"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span>Upload Artifact</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}